import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const blogGridSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  dataSource: z.enum(['manual', 'auto']).default('manual'),
  postType: z.string().max(80).optional(),
  limit: z.number().int().min(1).max(48).optional(),
  posts: z
    .array(
      z.object({
        image: z.string().min(1).max(1000),
        alt: z.string().max(200).optional(),
        category: z.string().min(1).max(80),
        title: z.string().min(1).max(300),
        excerpt: z.string().max(500),
        author: z.string().min(1).max(120),
        date: z.string().min(1).max(40),
        readTime: z.string().max(40).optional(),
        href: z.string().min(1).max(500),
      }),
    )
    .max(24)
    .optional(),
});

export type BlogGridData = z.infer<typeof blogGridSchema>;

export function BlogGridRenderer({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  posts,
}: BlogGridData) {
  const safePosts = Array.isArray(posts) ? posts : [];
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  return (
    <section className="v5-light v5-section v5-blog-section">
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-blog-h2">{title}</h2>
          {subtitle ? <p className="v5-listing-sub">{subtitle}</p> : null}
        </div>
        <div className="v5-blog-grid">
          {safePosts.map((p, i) => (
            <a key={i} href={p.href} className="v5-blog-card-link">
              <article className="v5-blog-card">
                <div className="v5-blog-img-wrap">
                  <img src={p.image} alt={p.alt ?? p.title} loading="lazy" />
                </div>
                <div className="v5-blog-body">
                  <div className="v5-blog-cat">{p.category}</div>
                  <h3 className="v5-blog-title">{p.title}</h3>
                  <p className="v5-blog-excerpt">{p.excerpt}</p>
                  <div className="v5-blog-meta">
                    <span>{p.author}</span>
                    <span>·</span>
                    <span>{p.date}</span>
                    {p.readTime ? (
                      <>
                        <span>·</span>
                        <span>{p.readTime}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </article>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const blogGridBlock: BlockDefinition<BlogGridData> = {
  meta: {
    type: 'blog-grid',
    label: 'Grid de blog (v5)',
    description: 'Grid de artículos con imagen, categoría, título, excerpt y meta (autor, fecha, tiempo de lectura).',
    category: 'collection',
    icon: 'newspaper',
  },
  schema: blogGridSchema,
  defaults: {
    eyebrow: '● Últimos artículos',
    title: 'Lo más reciente del blog',
    align: 'left',
    dataSource: 'auto',
    postType: 'blog',
    limit: 9,
    posts: [
      {
        image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800&h=500&fit=crop&q=85&auto=format',
        alt: 'Pitch deck',
        category: 'Fundraising',
        title: 'Cómo preparar tu pitch deck para una ronda seed: guía paso a paso',
        excerpt: 'Las 11 slides que debe tener un pitch deck que cierre conversaciones.',
        author: 'Equipo editorial',
        date: 'Abril 2026',
        readTime: '8 min',
        href: '/blog/pitch-deck-ronda-seed',
      },
      {
        image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop&q=85&auto=format',
        alt: 'Connect to Value',
        category: 'M&A',
        title: 'Connect to Value®: las 6 fases que estructuran cada operación',
        excerpt: 'Detrás del 92% de tasa de cierre hay un proceso.',
        author: 'Head of M&A',
        date: 'Abril 2026',
        readTime: '12 min',
        href: '/blog/connect-to-value-metodologia',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'dataSource',
      label: 'Origen de los artículos',
      type: 'select',
      options: [
        { label: 'Automático (últimos publicados)', value: 'auto' },
        { label: 'Manual (los defino abajo)', value: 'manual' },
      ],
    },
    { name: 'postType', label: 'Post type (solo si auto)', type: 'text', max: 80 },
    { name: 'limit', label: 'Nº máximo (solo si auto)', type: 'number' },
    {
      name: 'posts',
      label: 'Artículos (solo si manual)',
      type: 'array',
      max: 24,
      itemFields: [
        { name: 'image', label: 'Imagen', type: 'image', required: true },
        { name: 'alt', label: 'Alt', type: 'text', max: 200 },
        { name: 'category', label: 'Categoría', type: 'text', required: true, max: 80 },
        { name: 'title', label: 'Título', type: 'text', required: true, max: 300 },
        { name: 'excerpt', label: 'Excerpt', type: 'textarea', required: true, max: 500 },
        { name: 'author', label: 'Autor', type: 'text', required: true, max: 120 },
        { name: 'date', label: 'Fecha', type: 'text', required: true, max: 40 },
        { name: 'readTime', label: 'Tiempo lectura', type: 'text', max: 40 },
        { name: 'href', label: 'URL post', type: 'url', required: true },
      ],
    },
  ],
  Renderer: BlogGridRenderer,
};
